// theme.ts
import { extendTheme, type ThemeConfig } from "@chakra-ui/react"
import { numberInputAnatomy } from "@chakra-ui/anatomy"

const config: ThemeConfig = {
  initialColorMode: "light",
  useSystemColorMode: false,
}

// Client palette
const clientPalette = {
  50:  "#ffffff",
  100: "#fedfdc",
  200: "#febbb3",
  300: "#fe9286",
  400: "#ff7a67",
  500: "#fe624e",
  600: "#e05542",
  700: "#c64a39",
}

const focusRing = {
  borderColor: "brand.500",
  boxShadow: "0 0 0 1px var(--chakra-colors-brand-500)",
  outline: "none",
}

const theme = extendTheme({
  config,
  colors: { brand: clientPalette, primary: clientPalette },
  fonts: {
    heading: "Inter, sans-serif",
    body: "Inter, sans-serif",
  },
  styles: {
    global: {
      ":root": {
        "--color-1": "#ffffff",
        "--color-2": "#fedfdc",
        "--color-3": "#febbb3",
        "--color-4": "#fe9286",
        "--color-5": "#fe624e",
      },
      ".color-1": { color: "var(--color-1)" },
      ".color-2": { color: "var(--color-2)" },
      ".color-3": { color: "var(--color-3)" },
      ".color-4": { color: "var(--color-4)" },
      ".color-5": { color: "var(--color-5)" },
      body: { bg: "brand.50", color: "black" },
    },
  },
  components: {
    Card: { baseStyle: { container: { bg: "brand.50", color: "black" } } },
    Button: {
      baseStyle: { fontWeight: "semibold" },
      variants: {
        solid: {
          bg: "brand.500",
          color: "white",
          _hover: { bg: "brand.600" },
          _active: { bg: "brand.700" },
        },
        outline: {
          borderColor: "brand.500",
          color: "brand.500",
          _hover: { bg: "brand.100" },
        },
      },
      defaultProps: { variant: "solid", colorScheme: "brand" },
    },
    Modal: { baseStyle: { dialog: { bg: "brand.50", color: "black" } } },

    // Inputs
    Input: {
      variants: {
        outline: {
          field: {
            borderColor: "brand.200",
            _hover: { borderColor: "brand.300" },
            _focus: focusRing,
            _focusVisible: focusRing,
            _invalid: {
              borderColor: "red.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-red-500)",
            },
          },
        },
        filled: { field: { _focus: focusRing, _focusVisible: focusRing } },
        flushed: { field: { _focus: { borderColor: "brand.500" }, _focusVisible: { borderColor: "brand.500" } } },
      },
      defaultProps: { variant: "outline" },
    },
    Select: {
      variants: {
        outline: {
          field: {
            bg: "white",
            borderColor: "brand.200",
            _hover: { borderColor: "brand.300" },
            _focus: focusRing,
            "&[data-focus]": focusRing,
          },
          icon: { color: "brand.500" },
        },
      },
      defaultProps: { variant: "outline", focusBorderColor: "brand.500" },
    },

    // ✅ NumberInput (multi-part)
    NumberInput: {
      // not strictly required, but documents the parts:
      parts: numberInputAnatomy.keys, // ["root","field","stepperGroup","stepper"]
      variants: {
        outline: {
          field: {
            bg: "white",
            borderColor: "brand.200",
            _hover: { borderColor: "brand.300", bg: "white" }, // keep bg stable
            _focus: focusRing,
            _focusVisible: focusRing,
            _invalid: {
              borderColor: "red.500",
              boxShadow: "0 0 0 1px var(--chakra-colors-red-500)",
            },
          },
          stepperGroup: { bg: "transparent" },
          stepper: {
            bg: "transparent",
            color: "brand.500",
            _hover: { bg: "brand.100", color: "brand.600" },
            _active: { bg: "transparent", color: "brand.700" },
            _disabled: { opacity: 0.4, bg: "transparent" },
          },
        },
      },
      defaultProps: { variant: "outline" },
    },
  },
})

export default theme
